import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useFormikContext } from 'formik'

import NetworkWizardLayout from '../../NetworkWizardLayout'
import GoogleMap from 'components/GoogleMaps/GoogleMap'
import {
  getGoogleMapLink,
  GoogleMapsDataFromLatLng,
  loadGoogleMapsDropdownOptions,
} from 'components/GoogleMaps/utils'
import { commonMessages } from 'app/i18n/messages'
import { network } from 'features/networkAi/messages'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import AsyncDropdownField from 'components/Fields/AsyncDropdownField/AsyncDropdownField'
import { CommonLink } from 'components/Button'
import { SpinnerCentered } from 'components/Loader'
import { AddNetworkFormValuesType } from '../types'
import {
  NetworkWizardBottomPanel,
  NetworkWizardCancelButton,
  NetworkWizardErrorWrapper,
  NetworkWizardJobNameBlock,
  NetworkWizardSecondaryTitle,
  NetworkWizardSubmitButton,
  NetworkWizardTextField,
  NetworkWizardTitle,
  NetworkWizardWhiteBlock,
  NetworkWizardWhiteBlockBody,
  NetworkWizardWhiteBlockHeader,
} from '../../styles'
import {
  AddNetworkContainer,
  AddNetworkFormContainer,
  AddNetworkGoogleMapWrapper,
} from './styles'

interface AddNetworkFormProps {
  //TODO: job must have type DomainOverload from SWC but there is bug that is why 'any' for now
  job?: any
  handleCloseWizard: () => void
}

function AddNetworkForm({ job, handleCloseWizard }: AddNetworkFormProps) {
  const { formatMessage } = useIntl()
  const { locale } = useContext(LocalizationContext)
  const {
    values,
    setFieldValue,
    isValid,
    status,
    setStatus,
    isSubmitting,
    setFieldTouched,
  } = useFormikContext<AddNetworkFormValuesType>()
  const isSubmitDisabled =
    isSubmitting || !isValid || !values.jobName || !values.location
  const [currentLatitude, setCurrentLatitude] = useState<number>(
    job?.location?.latitude
  )
  const [currentLongitude, setCurrentLongitude] = useState<number>(
    job?.location?.longitude
  )

  useEffect(() => {
    setCurrentLatitude(job?.location?.latitude)
  }, [job?.location?.latitude])

  useEffect(() => {
    setCurrentLongitude(job?.location?.longitude)
  }, [job?.location?.longitude])

  const setIsError = useCallback(() => {
    setStatus({ error: true })
  }, [setStatus])

  const initialDropdownValue = useMemo(
    () => ({
      label: values?.location?.formatted_address,
      value: values?.location,
    }),
    [values?.location]
  )

  useEffect(() => {
    const getInitialGoogleMapLocation = async () => {
      try {
        setStatus({ isLoading: true })
        const googleMapsData = await GoogleMapsDataFromLatLng(
          currentLatitude,
          currentLongitude,
          locale
        )
        // Google can return several addresses for provided coordinates and we are taking the first one - results?.[0]
        setFieldValue('location', googleMapsData?.results?.[0])
        setStatus({ isLoading: false })
      } catch (err) {
        setIsError()
        console.log('Error getting initial google maps data', err)
      }
    }

    if (currentLatitude && currentLongitude) {
      getInitialGoogleMapLocation()
    }
  }, [
    locale,
    currentLatitude,
    currentLongitude,
    setFieldValue,
    setIsError,
    setStatus,
  ])

  const loadOptions = async (inputValue: string) =>
    await loadGoogleMapsDropdownOptions({
      inputValue,
      onError: setIsError,
      locale,
    })

  const bottomBlock = useMemo(() => {
    return (
      <NetworkWizardBottomPanel>
        {isSubmitting || status.isLoading ? (
          <SpinnerCentered size="40" />
        ) : (
          <>
            <NetworkWizardCancelButton
              variant="secondary"
              type="button"
              onClick={handleCloseWizard}
            >
              {formatMessage(commonMessages.cancel)}
            </NetworkWizardCancelButton>

            <NetworkWizardSubmitButton
              variant="primary"
              type="submit"
              disabled={isSubmitDisabled}
            >
              {formatMessage(commonMessages.next)}
            </NetworkWizardSubmitButton>
          </>
        )}
      </NetworkWizardBottomPanel>
    )
  }, [
    formatMessage,
    handleCloseWizard,
    isSubmitDisabled,
    isSubmitting,
    status.isLoading,
  ])

  return (
    <AddNetworkFormContainer>
      <NetworkWizardLayout bottomBlock={bottomBlock} currentWizardStepId={1}>
        <AddNetworkContainer>
          <NetworkWizardTitle>
            {formatMessage(network.addTitle)}
          </NetworkWizardTitle>

          {status.error && (
            <NetworkWizardWhiteBlock>
              <NetworkWizardErrorWrapper>
                Error:{' '}
                {status.errorMessage ||
                  formatMessage(commonMessages.commonError)}
              </NetworkWizardErrorWrapper>
            </NetworkWizardWhiteBlock>
          )}

          <NetworkWizardJobNameBlock>
            <NetworkWizardWhiteBlockHeader>
              <NetworkWizardSecondaryTitle>
                {formatMessage(network.titleName)}
              </NetworkWizardSecondaryTitle>
            </NetworkWizardWhiteBlockHeader>
            <NetworkWizardWhiteBlockBody>
              <NetworkWizardTextField
                name="jobName"
                placeholder="Job Name"
                showLabel
                disabled={job?.name}
              />
            </NetworkWizardWhiteBlockBody>
          </NetworkWizardJobNameBlock>

          <NetworkWizardWhiteBlock>
            <NetworkWizardWhiteBlockHeader>
              <NetworkWizardSecondaryTitle>
                Enter in an Address
              </NetworkWizardSecondaryTitle>
            </NetworkWizardWhiteBlockHeader>
            <NetworkWizardWhiteBlockBody>
              <AsyncDropdownField
                initialValue={initialDropdownValue}
                name="location"
                placeholder="Address"
                loadOptions={loadOptions}
                onBlur={() => setFieldTouched('location', true)}
                handleChange={(location: any) => {
                  setFieldValue('isLocationChanged', true)
                  setCurrentLatitude(location?.value?.geometry?.location?.lat)
                  setCurrentLongitude(location?.value?.geometry?.location?.lng)
                }}
                disabled={status.isLoading}
              />

              {currentLongitude && currentLatitude && (
                <CommonLink
                  as="a"
                  href={getGoogleMapLink(currentLatitude, currentLongitude)}
                  target="_blank"
                >
                  <AddNetworkGoogleMapWrapper>
                    <GoogleMap
                      latitude={currentLatitude}
                      longitude={currentLongitude}
                      height={360}
                      width={480}
                    />
                  </AddNetworkGoogleMapWrapper>
                </CommonLink>
              )}
            </NetworkWizardWhiteBlockBody>
          </NetworkWizardWhiteBlock>
        </AddNetworkContainer>
      </NetworkWizardLayout>
    </AddNetworkFormContainer>
  )
}

export default AddNetworkForm
